import {
  AppShell,
  Box,
  Button,
  Stack,
  UnstyledButton,
  Text,
  Indicator,
  Avatar,
  Burger,
  Paper,
  Menu,
  Divider,
  Title,
  Popover,
  Group,
  useMantineTheme,
} from "@mantine/core";
import { JasperWordmark } from "@jasper/forms";
import { useMatch, useNavigate } from "react-router-dom";
import {
  IconClipboardList,
  IconLayoutDashboard,
  IconPhoneCall,
  IconPlus,
  IconSettings,
} from "@tabler/icons-react";
import { useState } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { ORDER_FORM_V2_FLAG } from "@jasper/product-workflows";

import { useAuthContext } from "../../../shared/contexts/AuthContext";
import { UserGroupType, UserType } from "../../../generated/graphql";
import { JASPER_HOTLINE_PHONE_NUMBER, LayoutDrawerProps } from ".";
import { useMediaQuery } from "@mantine/hooks";

interface NavigationButtonProps {
  label: string;
  path: string;
  icon: React.ReactNode;
  indicatorLabel?: string;
  queryParams?: Record<string, string>;
  onLinkClick?: () => void;
}

const NavigationButton = ({
  label,
  path,
  queryParams,
  icon,
  indicatorLabel,
  onLinkClick,
}: NavigationButtonProps) => {
  const navigate = useNavigate();
  const isActive = useMatch(path);

  return (
    <Indicator
      label={indicatorLabel}
      position="middle-end"
      color="cardBackground"
      autoContrast
      size={16}
      offset={16}
      disabled={!indicatorLabel}
    >
      <Button
        variant={isActive ? "light" : "subtle"}
        justify="flex-start"
        onClick={() => {
          navigate({
            pathname: path,
            search: new URLSearchParams(queryParams).toString(),
          });
          onLinkClick?.();
        }}
        leftSection={icon}
        w="100%"
      >
        {label}
      </Button>
    </Indicator>
  );
};

const AccountButton = ({ onLinkClick }: LayoutDrawerProps) => {
  const { logOut, user } = useAuthContext();
  const [isAccountButtonHovered, setIsAccountButtonHovered] = useState(false);
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  if (!user) {
    return null;
  }

  const userTitle = user.type === UserType.Dentist ? "Dr." : "";
  const groupTitle =
    user.userGroup?.groupType === UserGroupType.Dentist ? "Cabinet" : "";

  return (
    <Menu position={isMobile ? "top" : "right"} floatingStrategy="fixed">
      <Menu.Target>
        <UnstyledButton
          w="100%"
          onMouseEnter={() => setIsAccountButtonHovered(true)}
          onMouseLeave={() => setIsAccountButtonHovered(false)}
        >
          <Paper bg={isAccountButtonHovered ? "brand.0" : undefined} p="s3">
            <Group wrap="nowrap">
              <Avatar
                color="brand"
                name={`${user.firstName} ${user.lastName}`}
              />
              <Stack gap={0}>
                <Text fw={600}>
                  {userTitle} {user.firstName} {user.lastName}
                </Text>
                <Text size="sm">
                  {groupTitle} {user.userGroup?.name}
                </Text>
              </Stack>
            </Group>
          </Paper>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          onClick={() => {
            navigate("/settings");
            onLinkClick?.();
          }}
        >
          Paramètres du compte
        </Menu.Item>
        <Divider />
        <Menu.Item
          onClick={() => {
            onLinkClick?.();
            logOut();
          }}
        >
          Se déconnecter
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

const HotlineButton = () => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  return (
    <Popover
      width={200}
      position={isMobile ? "bottom" : "right"}
      withArrow
      shadow="md"
      floatingStrategy="fixed"
    >
      <Popover.Target>
        <Button
          variant="subtle"
          justify="flex-start"
          leftSection={<IconPhoneCall />}
        >
          Hotline
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Title order={6}>Contacter la hotline</Title>
        <Text size="xs">
          Pour contacter notre équipe de support, veuillez appeler au numéro :
          {JASPER_HOTLINE_PHONE_NUMBER}
        </Text>
      </Popover.Dropdown>
    </Popover>
  );
};

const LogoButton = ({ onLinkClick }: { onLinkClick?: () => void }) => {
  const navigate = useNavigate();

  return (
    <Box py="s3" px="s2">
      <UnstyledButton
        onClick={() => {
          navigate("/");
          onLinkClick?.();
        }}
        display="block"
      >
        <JasperWordmark size={21} style={{ display: "block" }} />
      </UnstyledButton>
      {import.meta.env.MODE !== "production" && (
        <Text size="sm" c="amber">
          {import.meta.env.MODE}
        </Text>
      )}
    </Box>
  );
};

const NewOrderButton = ({ onLinkClick }: { onLinkClick?: () => void }) => {
  const navigate = useNavigate();
  const isOrderFormV2Enabled = useFeatureFlagEnabled(ORDER_FORM_V2_FLAG);
  const { user } = useAuthContext();

  if (
    user?.type !== UserType.Jasper &&
    import.meta.env.MODE === "production" &&
    !user?.allowPhysicalPrint
  ) {
    return null;
  }
  return (
    <Button
      onClick={() => {
        navigate(isOrderFormV2Enabled ? "/orders/new" : "/create-order-form");
        onLinkClick?.();
      }}
      leftSection={<IconPlus />}
    >
      Nouvelle commande
    </Button>
  );
};

export const LayoutDrawerV2 = ({ onLinkClick, onClose }: LayoutDrawerProps) => {
  return (
    <AppShell.Navbar bg="cardBackground">
      <Stack h="100%" w="100%" py="s5" px="s4" gap="s6" justify="space-between">
        <Stack>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <LogoButton onLinkClick={onLinkClick} />
            <Burger
              opened
              onClick={onClose}
              hiddenFrom="xs"
              size="sm"
              aria-label="Fermer le menu"
            />
          </Group>
          <Stack gap="s5">
            <NewOrderButton onLinkClick={onLinkClick} />

            <Stack gap="s4">
              <Stack gap="s3">
                <NavigationButton
                  label="Tableau de bord"
                  path="/"
                  icon={<IconLayoutDashboard />}
                  onLinkClick={onLinkClick}
                />
                <NavigationButton
                  label="Commandes"
                  path="/orders"
                  icon={<IconClipboardList />}
                  onLinkClick={onLinkClick}
                />
              </Stack>

              <Stack gap="s3">
                <Text variant="eyebrow" c="ink" px="s3">
                  Gestion
                </Text>
                <NavigationButton
                  label="Préférences"
                  path="/settings"
                  queryParams={{ tab: "ProductPreferences" }}
                  icon={<IconSettings />}
                  onLinkClick={onLinkClick}
                />
                <HotlineButton />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <AccountButton onLinkClick={onLinkClick} />
      </Stack>
    </AppShell.Navbar>
  );
};
